import { ChangeEvent, FormEvent, HTMLAttributes, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import {
  Chip,
  MenuItem,
  Theme,
  useMediaQuery,
  Slider,
  Drawer,
  Autocomplete as MuiAutocomplete,
  TextField,
} from '@mui/material';

import { ScholarshipCardSkeleton } from '@components/ScholarshipCard/ScholarshipCardSkeleton';
import {
  CoreBox,
  CoreTooltip,
  CoreFormGroup,
  CoreCheckbox,
  CoreFormControlLabel,
  CoreAlert,
  CoreTypography,
} from '@youscience/core';

import ActionButton from '@components/ActionButton';

import { omit } from 'lodash-es';

import useScholarshipListStore, { MainFilters, ScholarshipListFilters } from '@store/AllScholarshipsStore';

import { AutocompleteOption, Option } from '@interfaces';

import { FILTER_LABEL_BY_VALUE, FILTERS_DATA, MAIN_FILTERS_VALUES } from '@constants/allScholarships';
import { SortByOptions, SCHOLARSHIP_CARD_SKELETONS } from '@constants/scholarships';
import { ALL_STATES_OPTIONS } from '@constants/scholarshipDetail';

import { separateDigitsByComma } from '@utils/formatter';

import { Pagination } from '@components/Pagination';
import ScholarshipCard from '@components/ScholarshipCard';
import { FilterAccordion } from '@components/FilterAccordion';
import { EmptyStateText } from '@components/EmptyStateText';
import { renderNoOptionsText } from '@components/Form/Autocomplete/Autocomplete';

import { Search } from '@components/Search';

import RefreshIcon from '@images/refresh.svg?react';
import SearchFilesIcon from '@images/search-files.svg?react';
import InfoIcon from '@images/info.svg?react';
import MobileFilterIcon from '@images/filter.svg?react';
import CloseIcon from '@images/close.svg?react';
import PlusIcon from '@images/accordion-plus.svg?react';
import MinusIcon from '@images/accordion-minus.svg?react';

import SortBy, { SortByValues } from '../SortBy';
import { INITIAL_VALUE } from '../SortBy/SortBy';
import { useStateSpotlight } from '@hooks/useStateSpotlight';
import { UsheIcon } from '@components/UsheIcon';
import {
  REST_OF_US_FILTER,
  REST_OF_US_LABEL,
  STATE_PUBLIC_COLLEGE_FILTER,
  ScholarshipSortOption,
  getStatePublicCollegeFilter,
  getStatePublicCollegesLabel,
  getStateSpotlightScholarshipSortOptions,
} from '@constants/stateSpotlight';
import {
  StateSpotlightLocationKey,
  StateSpotlightLocationOptions,
  StatePublicCollegesNotice,
  getStateSpotlightLocationValue,
} from '@components/StateSpotlight';
import { ScholarshipSort, SortDirection } from '@common-types';

import {
  sxStyles,
  StyledMainSectionActions,
  StyledScholarshipCardsWrap,
  StyledBottomPaginationWrap,
  StyledDesktopFiltersWrap,
  StyledFilterSectionWrap,
  StyledMobileFilters,
  StyledMoreFiltersBtn,
} from './AllScholarships.styles';

import { usePagination } from '@hooks/usePagination';
import { useMobileFilters } from '@hooks/useMobileFilters';

export const AllScholarships = () => {
  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isTabletScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const {
    scholarshipsData,
    filters,
    awardsFilter,
    isLoading: isScholarshipsLoading,
    sortByValues,
    setSortByFields,
    getAwardsRange,
    getScholarshipsData,
    setScholarshipFilters,
    removeScholarshipFilter,
    removeScholarshipFilterValue,
    resetScholarshipFilters,
  } = useScholarshipListStore((state) => state);

  const { spotlight, shouldApplyDefaults, isWaitingForDefaults, markDefaultsApplied, hasCarriedFilters } =
    useStateSpotlight();
  const isWaitingForSpotlight = isWaitingForDefaults('scholarships');
  const isLoading = isScholarshipsLoading || isWaitingForSpotlight;

  const sortByOptions = useMemo(
    () => (spotlight ? getStateSpotlightScholarshipSortOptions(spotlight.stateName) : SortByOptions),
    [spotlight],
  );

  const { anchorMobileFilters, handleOpenMobileFilters, handleCloseMobileFilters } = useMobileFilters();
  const [selectedFilterChips, setSelectedFilterChips] = useState<Option[]>([]);
  const [isShowMoreFilters, setIsShowMoreFilters] = useState(false);
  const [states, setStates] = useState<AutocompleteOption[]>(filters?.stateResidence || []);
  const [searchFilterValue, setSearchFilterValue] = useState(filters.keywords || '');
  const [isShowSearchAlert, setIsShowSearchAlert] = useState(false);
  const [range, setRange] = useState<number[]>([]);

  const { currentPage, paginationLimit, resetPaginationPage, handlePageSizeChange, handlePaginationChange } =
    usePagination<keyof ScholarshipListFilters>({
      initialLimit: Number(filters.limit),
      initialSkip: Number(filters.skip || 0),
      isMobileScreen,
      isLoading,
      setSearchFilters: setScholarshipFilters,
      removeSearchFilter: removeScholarshipFilter,
    });

  const handleSelectState = (event: SyntheticEvent, state: AutocompleteOption[]) => {
    setScholarshipFilters({ stateResidence: state });

    void setStates(state);
  };

  const omittedSelectedFilters = useMemo(() => omit(filters, ['skip', 'limit']), [filters]);

  const additionalFiltersCount = useMemo(() => {
    const additionalFilters = omit(omittedSelectedFilters, [
      ...MAIN_FILTERS_VALUES,
      STATE_PUBLIC_COLLEGE_FILTER,
      REST_OF_US_FILTER,
    ]);
    let count = filters.stateResidence?.length || 0;

    Object.values(additionalFilters).forEach((value) => {
      if (value) {
        count += 1;
      }
    });

    return count;
  }, [omittedSelectedFilters]);

  const marks = [
    { label: `$${separateDigitsByComma.format(awardsFilter.minLimit)}`, value: awardsFilter.minLimit },
    { label: `$${separateDigitsByComma.format(awardsFilter.maxLimit)}`, value: awardsFilter.maxLimit },
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleShowMoreFilters = () => {
    setIsShowMoreFilters((prevState) => !prevState);
  };

  const getSelectedFiltersFromState = () => {
    const options: Option[] = [];

    Object.keys(omittedSelectedFilters).forEach((value) => {
      if (!filters[value as keyof ScholarshipListFilters]) return;

      if (value === FILTERS_DATA.moneyRange.fieldValue) {
        const [min, max] = filters[value as keyof ScholarshipListFilters] as number[];

        if (!min || !max) return;

        options.push({
          value,
          label: `$${separateDigitsByComma.format(min)} - $${separateDigitsByComma.format(max)}`,
        });
      } else if (value === FILTERS_DATA.stateResidence.fieldValue) {
        states.forEach((item) => options.push(item));
      } else if (value === STATE_PUBLIC_COLLEGE_FILTER) {
        if (spotlight) {
          options.push({ value, label: getStatePublicCollegesLabel(spotlight.stateName) });
        }
      } else if (value === REST_OF_US_FILTER) {
        if (spotlight) {
          options.push({ value, label: REST_OF_US_LABEL });
        }
      } else if (filters.keywords && value === FILTERS_DATA.keywords.fieldValue) {
        options.push({
          value,
          label: filters.keywords,
        });
      } else {
        options.push({
          value,
          label: FILTER_LABEL_BY_VALUE[value],
        });
      }
    });

    return options;
  };

  const handleDeleteFilterChip = (value: keyof ScholarshipListFilters) => {
    void removeScholarshipFilterValue(value);

    if (value === FILTERS_DATA.moneyRange.fieldValue) {
      setRange([awardsFilter.minLimit, awardsFilter.maxLimit]);
    } else if (value === FILTERS_DATA.keywords.fieldValue) {
      setSearchFilterValue('');
    } else if (states.some((item) => item.value === value)) {
      const preparedStates = states.filter((item) => item.value !== value);

      setScholarshipFilters({ stateResidence: preparedStates });

      setStates(preparedStates);
    }

    resetPaginationPage();
  };

  const handleChangeStateSpotlightLocation = (key: StateSpotlightLocationKey, checked: boolean) => {
    if (checked && spotlight) {
      setScholarshipFilters({ [key]: getStateSpotlightLocationValue(key, spotlight) });
    } else {
      removeScholarshipFilter(key);
    }

    resetPaginationPage();
  };

  const handleResetFilters = () => {
    setRange([awardsFilter.minLimit, awardsFilter.maxLimit]);
    setStates([]);
    setSearchFilterValue('');

    void resetScholarshipFilters();

    resetPaginationPage();
  };

  const handleCheckBox = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setScholarshipFilters({ [event.target.value]: checked });

    resetPaginationPage();
  };

  const handleChangeCommitted = (event: SyntheticEvent | Event, fieldValue: number | number[]) => {
    if (
      Array.isArray(fieldValue) &&
      fieldValue[0] === awardsFilter.minLimit &&
      fieldValue[1] === awardsFilter.maxLimit
    ) {
      setScholarshipFilters({ moneyRange: [] });
    } else {
      setScholarshipFilters({ moneyRange: fieldValue as number[] });
    }

    resetPaginationPage();
  };

  const handleChangeRange = (event: Event, fieldValue: number | number[]) => {
    setRange(fieldValue as number[]);
  };

  const handleChangeSort = (sortByValues: SortByValues<ScholarshipSortOption>) => {
    setSortByFields(sortByValues);

    resetPaginationPage();
  };

  const handleResetSearch = () => {
    removeScholarshipFilterValue(FILTERS_DATA.keywords.fieldValue! as keyof ScholarshipListFilters);

    handleOffSearchAlert();
    setSearchFilterValue('');

    resetPaginationPage();
  };

  const handleSchoolSearch = (value: string) => {
    let keywords: string | undefined = value?.trim();

    if (keywords.length < 3) {
      setIsShowSearchAlert(true);
      keywords = undefined;
    } else {
      handleOffSearchAlert();
    }

    if (keywords) {
      setScholarshipFilters({ keywords });
    } else {
      removeScholarshipFilterValue(FILTERS_DATA.keywords.fieldValue! as keyof ScholarshipListFilters);
    }

    setSearchFilterValue(value);

    resetPaginationPage();
  };

  const handleOffSearchAlert = () => setIsShowSearchAlert(false);

  const handleFiltersSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  useEffect(() => {
    const options = getSelectedFiltersFromState();

    setSelectedFilterChips(options);
  }, [filters]);

  useEffect(() => {
    if (isWaitingForSpotlight) return;

    void getScholarshipsData(filters);
  }, [filters, isWaitingForSpotlight]);

  useEffect(() => {
    void getAwardsRange();
  }, []);

  useEffect(() => {
    if (!spotlight || !shouldApplyDefaults('scholarships')) {
      return;
    }

    markDefaultsApplied('scholarships');

    if (!hasCarriedFilters('scholarships')) {
      setScholarshipFilters({ [STATE_PUBLIC_COLLEGE_FILTER]: [spotlight.stateCode] });
    }

    if (sortByValues.sort === INITIAL_VALUE.sort && sortByValues.sortDirection === INITIAL_VALUE.sortDirection) {
      handleChangeSort({ sort: ScholarshipSort.UTAH, sortDirection: SortDirection.ASC });
    }
  }, [spotlight]);

  useEffect(() => {
    const rangeValue = [awardsFilter.minLimit, awardsFilter.maxLimit];

    setRange(rangeValue);
  }, [awardsFilter]);

  const renderFilters = (isExpanded?: boolean) => {
    return (
      <StyledFilterSectionWrap onSubmit={handleFiltersSubmit}>
        <CoreBox sx={sxStyles.filterSectionTitleWrap}>
          <CoreTypography component="div" variant="h4">
            Filter by:
          </CoreTypography>

          <CoreTooltip title='Use quotation marks to match an exact phrase (e.g., "national merit"). Without quotes, keywords are matched individually.'>
            <CoreBox tabIndex={0}>
              <InfoIcon />
            </CoreBox>
          </CoreTooltip>
        </CoreBox>

        <CoreBox sx={sxStyles.searchWrap}>
          <Search
            data-testid="scholarshipSearch"
            onEnterSearch={handleSchoolSearch}
            onClear={handleResetSearch}
            filterValue={searchFilterValue}
            onBlur={handleOffSearchAlert}
          />

          {isShowSearchAlert && (
            <CoreBox sx={sxStyles.alertWrap as object}>
              <CoreAlert severity="warning" description="Please enter at least three characters" />
            </CoreBox>
          )}
        </CoreBox>

        {scholarshipsData?.scholarships?.length > 0 && (
          <CoreTypography data-testid="resultCount" sx={sxStyles.filterSectionTitle} component="div">
            {isLoading ? 'Searching...' : `${scholarshipsData?.totalCount} results`}
          </CoreTypography>
        )}

        <CoreBox data-testid="filterChips" sx={sxStyles.chipsWrap}>
          {selectedFilterChips.map(({ value, label }) => {
            const deleteFilterItem = () => handleDeleteFilterChip(value as keyof ScholarshipListFilters);

            return (
              <CoreTooltip key={value} title={label}>
                <Chip
                  sx={sxStyles.filterChip}
                  icon={value === STATE_PUBLIC_COLLEGE_FILTER ? <UsheIcon /> : undefined}
                  variant={value === STATE_PUBLIC_COLLEGE_FILTER ? 'outlined' : 'filled'}
                  label={label}
                  color="primary"
                  onDelete={deleteFilterItem}
                />
              </CoreTooltip>
            );
          })}
        </CoreBox>

        <CoreBox>
          {selectedFilterChips.length > 0 && (
            <ActionButton
              data-testid="clearAllFilters"
              sx={sxStyles.clearBtn}
              type="button"
              onClick={handleResetFilters}
              variant="text"
            >
              Clear all Filters
              <RefreshIcon />
            </ActionButton>
          )}
        </CoreBox>

        {spotlight && (
          <FilterAccordion data-testid="locationAccordion" isExpanded={isExpanded} accordionTitle="Location">
            <StateSpotlightLocationOptions
              spotlight={spotlight}
              filters={filters as Record<string, unknown>}
              onChange={handleChangeStateSpotlightLocation}
            />
          </FilterAccordion>
        )}

        <FilterAccordion
          data-testid="requirementsAccordion"
          isExpanded={isExpanded}
          accordionTitle={FILTERS_DATA.requirements.title}
        >
          <CoreFormGroup sx={sxStyles.checkboxGroup}>
            {Object.values(FILTERS_DATA.requirements.fields!).map(({ value, label }) => (
              <CoreFormControlLabel
                key={value}
                control={
                  <CoreCheckbox
                    data-testid={value}
                    value={value}
                    checked={filters[value as keyof MainFilters] as boolean}
                    onChange={handleCheckBox}
                  />
                }
                label={label}
              />
            ))}
          </CoreFormGroup>
        </FilterAccordion>

        <FilterAccordion data-testid="typeAccordion" isExpanded={isExpanded} accordionTitle={FILTERS_DATA.type.title}>
          <CoreFormGroup sx={sxStyles.checkboxGroup}>
            {Object.values(FILTERS_DATA.type.fields!).map(({ value, label }) => (
              <CoreFormControlLabel
                key={value}
                control={
                  <CoreCheckbox
                    data-testid={value}
                    value={value}
                    checked={filters[value as keyof MainFilters] as boolean}
                    onChange={handleCheckBox}
                  />
                }
                label={label}
              />
            ))}
          </CoreFormGroup>
        </FilterAccordion>

        <CoreBox sx={sxStyles.moneyRangeWrap}>
          <CoreBox sx={sxStyles.moneyRangeTitleWrap}>
            <CoreTypography sx={sxStyles.moneyRangeTitle} component="div" variant="overline">
              {FILTERS_DATA.moneyRange.title}
            </CoreTypography>

            <CoreTooltip title="The dollar value of each award per individual applicant within the program">
              <CoreBox tabIndex={0}>
                <InfoIcon />
              </CoreBox>
            </CoreTooltip>
          </CoreBox>

          <Slider
            marks={marks}
            value={range}
            valueLabelDisplay="auto"
            step={awardsFilter.step}
            min={awardsFilter.minLimit}
            max={awardsFilter.maxLimit}
            onChange={handleChangeRange}
            onChangeCommitted={handleChangeCommitted}
            valueLabelFormat={(value) => `$${separateDigitsByComma.format(value)}`}
          />
        </CoreBox>

        <FilterAccordion
          data-testid="levelOfStudyAccordion"
          isExpanded={isExpanded}
          accordionTitle={FILTERS_DATA.levelOfStudy.title}
          tooltipInfoTitle="Award is meant for use during this year of use/level of study."
        >
          <CoreFormGroup sx={sxStyles.checkboxGroup}>
            {Object.values(FILTERS_DATA.levelOfStudy.fields!).map(({ value, label }) => (
              <CoreFormControlLabel
                key={value}
                control={
                  <CoreCheckbox
                    data-testid={value}
                    value={value}
                    checked={filters[value as keyof MainFilters] as boolean}
                    onChange={handleCheckBox}
                  />
                }
                label={label}
              />
            ))}
          </CoreFormGroup>
        </FilterAccordion>

        <FilterAccordion
          data-testid="institutionTypeAccordion"
          isExpanded={isExpanded}
          accordionTitle={FILTERS_DATA.institutionType.title}
        >
          <CoreFormGroup sx={sxStyles.checkboxGroup}>
            {Object.values(FILTERS_DATA.institutionType.fields!).map(({ value, label }) => (
              <CoreFormControlLabel
                key={value}
                control={
                  <CoreCheckbox
                    data-testid={value}
                    value={value}
                    checked={filters[value as keyof MainFilters] as boolean}
                    onChange={handleCheckBox}
                  />
                }
                label={label}
              />
            ))}
          </CoreFormGroup>
        </FilterAccordion>

        {isShowMoreFilters && (
          <>
            <FilterAccordion
              data-testid="locationAccordion"
              isExpanded={isExpanded}
              accordionTitle={FILTERS_DATA.stateResidence.title}
            >
              <MuiAutocomplete
                data-testid="stateResidence"
                sx={sxStyles.stateResidence}
                size="small"
                multiple
                options={ALL_STATES_OPTIONS}
                value={states}
                onChange={handleSelectState}
                renderInput={(params) => <TextField {...params} label="Select Location" placeholder="" />}
                renderOption={(optionProps: HTMLAttributes<HTMLLIElement>, option: AutocompleteOption) => (
                  <MenuItem
                    sx={sxStyles.autocompleteOption}
                    {...optionProps}
                    key={option.value}
                    disabled={option.isDisabled}
                  >
                    <span>{option.label}</span>
                  </MenuItem>
                )}
                clearIcon={null}
                noOptionsText={renderNoOptionsText()}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((item, index) => {
                    const tagProps = getTagProps({ index });

                    return (
                      <Chip
                        key={item.value}
                        size="small"
                        label={item.value}
                        className={tagProps.className}
                        disabled={tagProps.disabled}
                        onDelete={tagProps.onDelete}
                        tabIndex={tagProps.tabIndex}
                        data-tag-index={tagProps['data-tag-index']}
                      />
                    );
                  })
                }
              />
            </FilterAccordion>

            <FilterAccordion
              data-testid="awardClassificationAccordion"
              isExpanded={isExpanded}
              accordionTitle={FILTERS_DATA.awardClassification.title}
            >
              <CoreFormGroup sx={sxStyles.checkboxGroup}>
                {Object.values(FILTERS_DATA.awardClassification.fields!).map(({ value, label }) => (
                  <CoreFormControlLabel
                    key={value}
                    control={
                      <CoreCheckbox
                        data-testid={value}
                        value={value}
                        checked={filters[value as keyof MainFilters] as boolean}
                        onChange={handleCheckBox}
                      />
                    }
                    label={label}
                  />
                ))}
              </CoreFormGroup>
            </FilterAccordion>

            <FilterAccordion
              data-testid="areasOfStudyAccordion"
              isExpanded={isExpanded}
              accordionTitle={FILTERS_DATA.areasOfStudy.title}
            >
              <CoreBox sx={sxStyles.cipCodesWrap}>
                <CoreFormGroup sx={sxStyles.checkboxGroup}>
                  {Object.values(FILTERS_DATA.areasOfStudy.fields!).map(({ value, label }) => (
                    <CoreFormControlLabel
                      key={value}
                      control={
                        <CoreCheckbox
                          data-testid={value}
                          value={value}
                          checked={filters[value as keyof MainFilters] as boolean}
                          onChange={handleCheckBox}
                        />
                      }
                      label={
                        <CoreTooltip title={label}>
                          <CoreBox sx={sxStyles.areaLabel}>{label}</CoreBox>
                        </CoreTooltip>
                      }
                    />
                  ))}
                </CoreFormGroup>
              </CoreBox>
            </FilterAccordion>

            <FilterAccordion
              data-testid="ethnicityAccordion"
              isExpanded={isExpanded}
              accordionTitle={FILTERS_DATA.ethnicity.title}
            >
              <CoreFormGroup sx={sxStyles.checkboxGroup}>
                {Object.values(FILTERS_DATA.ethnicity.fields!).map(({ value, label }) => (
                  <CoreFormControlLabel
                    key={value}
                    control={
                      <CoreCheckbox
                        data-testid={value}
                        value={value}
                        checked={filters[value as keyof MainFilters] as boolean}
                        onChange={handleCheckBox}
                      />
                    }
                    label={label}
                  />
                ))}
              </CoreFormGroup>
            </FilterAccordion>

            <FilterAccordion
              data-testid="otherCategoriesAccordion"
              isExpanded={isExpanded}
              accordionTitle={FILTERS_DATA.otherCategories.title}
            >
              <CoreFormGroup sx={sxStyles.checkboxGroup}>
                {Object.values(FILTERS_DATA.otherCategories.fields!).map(({ value, label }) => (
                  <CoreFormControlLabel
                    key={value}
                    control={
                      <CoreCheckbox
                        data-testid={value}
                        value={value}
                        checked={filters[value as keyof MainFilters] as boolean}
                        onChange={handleCheckBox}
                      />
                    }
                    label={label}
                  />
                ))}
              </CoreFormGroup>
            </FilterAccordion>

            <FilterAccordion
              data-testid="disabilityAccordion"
              isExpanded={isExpanded}
              accordionTitle={FILTERS_DATA.disability.title}
            >
              <CoreFormGroup sx={sxStyles.checkboxGroup}>
                {Object.values(FILTERS_DATA.disability.fields!).map(({ value, label }) => (
                  <CoreFormControlLabel
                    key={value}
                    control={
                      <CoreCheckbox
                        data-testid={value}
                        value={value}
                        checked={filters[value as keyof MainFilters] as boolean}
                        onChange={handleCheckBox}
                      />
                    }
                    label={label}
                  />
                ))}
              </CoreFormGroup>
            </FilterAccordion>
          </>
        )}

        <StyledMoreFiltersBtn onClick={handleShowMoreFilters} data-testid="showMoreFilters">
          {isShowMoreFilters
            ? 'Show less filters'
            : `Show more filters ${additionalFiltersCount ? `(${additionalFiltersCount})` : ''} `}

          {isShowMoreFilters ? <MinusIcon /> : <PlusIcon />}
        </StyledMoreFiltersBtn>
      </StyledFilterSectionWrap>
    );
  };

  return (
    <CoreBox sx={sxStyles.mainSectionWrap}>
      <StyledDesktopFiltersWrap>{renderFilters()}</StyledDesktopFiltersWrap>

      <CoreBox sx={sxStyles.cardsWrap}>
        {isTabletScreen && (
          <StyledMobileFilters onClick={handleOpenMobileFilters}>
            <MobileFilterIcon />
          </StyledMobileFilters>
        )}

        <StyledMainSectionActions>
          <SortBy<ScholarshipSortOption>
            sortByValues={sortByValues}
            sortByOptions={sortByOptions}
            setSortByFields={handleChangeSort}
            dataTestId="sortBySelect"
          />

          <Pagination
            totalCount={scholarshipsData.totalCount ? scholarshipsData.totalCount : undefined}
            limit={paginationLimit}
            page={currentPage}
            isLoading={isLoading}
            onPaginationChange={handlePaginationChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </StyledMainSectionActions>

        {spotlight && getStatePublicCollegeFilter(filters as Record<string, unknown>) && (
          <StatePublicCollegesNotice spotlight={spotlight} />
        )}

        <StyledScholarshipCardsWrap>
          {isLoading
            ? SCHOLARSHIP_CARD_SKELETONS.map((skeletonCard) => {
                return <ScholarshipCardSkeleton key={skeletonCard.id} isWiderCardType={false} />;
              })
            : scholarshipsData?.scholarships?.map((scholarship) => (
                <ScholarshipCard key={scholarship.id} scholarship={scholarship} />
              ))}
        </StyledScholarshipCardsWrap>

        {scholarshipsData.scholarships?.length > 0 && (
          <StyledBottomPaginationWrap>
            <CoreBox />

            <Pagination
              totalCount={scholarshipsData.totalCount ? scholarshipsData.totalCount : undefined}
              limit={paginationLimit}
              page={currentPage}
              isLoading={isLoading}
              onPaginationChange={handlePaginationChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </StyledBottomPaginationWrap>
        )}

        {!isLoading && scholarshipsData.scholarships?.length === 0 && (
          <EmptyStateText
            icon={<SearchFilesIcon />}
            title="No results"
            subtitle="Hmmm. Looks like we couldn't find any matches. Please remove some filters and try again."
          />
        )}
      </CoreBox>

      <Drawer
        sx={sxStyles.mobileFilters}
        anchor="top"
        open={Boolean(anchorMobileFilters)}
        onClose={handleCloseMobileFilters}
      >
        {renderFilters(false)}

        <CoreBox sx={sxStyles.closeMobileFilter} onClick={handleCloseMobileFilters}>
          <CloseIcon />
        </CoreBox>
      </Drawer>
    </CoreBox>
  );
};
